import { ChangeEvent, RefObject, useCallback, useRef } from 'react';
import Textarea from 'components/ui/Textarea';
import { EMPTY_STRING } from 'constants/general';
import { ExtendedSuggestion } from 'interfaces/setting.interface';
import { t } from 'services/i18n.service';
import HabitEmojiPicker from './HabitEmojiPicker';
import HabitNameSuggestionsDropdown from './HabitNameSuggestionsDropdown';
import { useHabitNameSuggestions } from 'hooks/useHabitNameSuggestions';

interface HabitNameInputProps {
  emoji?: string;
  habitName: string;
  onEmojiClick?: () => void;
  onEmojiChange?: (emoji: string) => void;
  onNameChange?: (value: string) => void;
  onSuggestionSelect?: (suggestion: ExtendedSuggestion) => void;
  /** Routine bucket this row belongs to (ACTIVITY_TYPES.*), so break rows only get break suggestions. */
  activityType?: string;
  onLockedInteraction?: () => void;
  inputRef?: RefObject<HTMLTextAreaElement>;
  onBlur?: () => void;
  readOnly?: boolean;
  className?: string;
  hasError?: boolean;
}

const HabitNameInput: React.FC<HabitNameInputProps> = ({
  emoji = EMPTY_STRING,
  habitName = '',
  onEmojiClick,
  onEmojiChange,
  onNameChange,
  onSuggestionSelect,
  activityType,
  onLockedInteraction,
  readOnly = false,
  className = '',
  inputRef,
  onBlur
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { suggestions, updateSuggestions, clearSuggestions } =
    useHabitNameSuggestions(activityType);

  const handleNameChange = useCallback(
    (value: string) => {
      onNameChange?.(value);
      if (!readOnly && onSuggestionSelect) {
        updateSuggestions(value);
      }
    },
    [onNameChange, onSuggestionSelect, readOnly, updateSuggestions]
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: ExtendedSuggestion) => {
      onSuggestionSelect?.(suggestion);
      clearSuggestions();
    },
    [clearSuggestions, onSuggestionSelect]
  );

  const showSuggestions =
    !readOnly && Boolean(onSuggestionSelect) && suggestions.length > 0;

  return (
    <div
      ref={containerRef}
      className={`w-full flex items-start ~gap-2/3 relative ${className}`}>
      <HabitEmojiPicker
        emoji={emoji}
        readOnly={readOnly}
        onEmojiClick={() =>
          readOnly ? onLockedInteraction?.() : onEmojiClick?.()
        }
        onEmojiChange={onEmojiChange}
      />
      <Textarea
        placeholder={t('settings_page.enter_habit_name')}
        value={habitName}
        readOnly={readOnly}
        size='sm'
        resize='none'
        ref={inputRef}
        onBlur={onBlur}
        onFocus={() => (readOnly ? onLockedInteraction?.() : undefined)}
        {...(onNameChange && !readOnly
          ? {
              onChange: (event: ChangeEvent<HTMLTextAreaElement>) =>
                handleNameChange(event.target.value)
            }
          : {})}
      />
      {showSuggestions ? (
        <HabitNameSuggestionsDropdown
          suggestions={suggestions}
          anchorRef={containerRef}
          onSelect={handleSuggestionSelect}
          onDismiss={clearSuggestions}
        />
      ) : null}
    </div>
  );
};

export default HabitNameInput;
